import React from 'react';

import classNames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import grey from '@material-ui/core/colors/grey';
import { withStyles, createStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';

import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../../employee-groups/employee-group-selectors';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';
import {
  announcementsForOrgIdSelector,
  announcementsForOrgIdStatusSelector,
} from '../../reducers/message-selectors';
import { loadOrgAnnouncements } from '../actions/announcements/load-org-announcements';

import { EmployeeInvalidPhoneNumberWarning } from '../../employees/employee-invalid-phone-number-warning';
import { EmployeeNotificationsWarning } from '../../employees/employee-notifications-warning';
import { TextMessage } from '../../messages/legacy-components/text-message';
import { UiBreadcrumbHeader } from '../../ui/ui-breadcrumb-header';
import { UiButton } from '../../ui/ui-button';
import { employeeAnnouncementsRoute } from '../../web-routes';

import { LegacyAnnouncementComposer } from './legacy-announcement-composer';
import { LegacyEmployeeGroupAnnouncements } from './legacy-employee-group-announcements';

import { allEmployeesKey, allEmployeesLabel } from '../../util/all-employees-option';
import { HTMLStripper } from '../../util/html-stripper';
import {
  HeaderButtonAddIcon,
  ICON_NAMES,
  MaterialDesignIcon,
} from '../../util/material-design-icons';
import { LoadingMessage } from '../../widgets/loading-message';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    contacts: {
      width: '300px',
      border: `1px solid ${grey[300]}`,
      borderWidth: '1px 1px 0 0',
      overflowY: 'auto',
      flexShrink: 0,
    },
    contact: {
      borderBottom: `1px solid ${grey[300]}`,
      cursor: 'pointer',
      '&:hover': {
        background: grey[300],
      },
      '&.selected': {
        background: (theme as any).palette.primary[400],
        color: 'white',
      },
    },
    threadPane: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      background: 'white',
      borderTop: `1px solid ${grey[300]}`,
    },
    threadHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    threadContainer: {
      overflowY: 'auto',
      borderTop: `1px solid ${grey[300]}`,
    },
    thread: {
      maxWidth: '800px',
      margin: '0px auto 120px auto',
    },
    messageBody: {
      fontSize: '12px',
      marginTop: '3px',
    },
    mobile: {
      width: '100%',
      flexShrink: 0,
    },
    hidden: {
      display: 'none',
    },
  });

const mapStateToProps = (state: any, { orgId, queryParams }: InputProps) => {
  let activeGroupId: number | string | null = null;
  if (queryParams.groupId) {
    if (queryParams.groupId === allEmployeesKey(orgId)) {
      activeGroupId = queryParams.groupId;
    } else {
      const parsedGroupId = parseInt(queryParams.groupId, 10);
      if (!Number.isNaN(parsedGroupId)) {
        activeGroupId = parsedGroupId;
      }
    }
  }

  return {
    activeGroupId,
    user: userSelector(state),
    announcementsStatus: announcementsForOrgIdStatusSelector(state, orgId),
    announcements: announcementsForOrgIdSelector(state, orgId),
    employees: employeesForOrgIdSelector(state, orgId),
    employeeGroups: employeeGroupsForOrgIdSelector(state, orgId),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrgAnnouncements: () => dispatch(loadOrgAnnouncements(orgId)),
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
    loadOrgEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type InputProps = {
  orgId: number;
  queryParams: {
    groupId?: string;
  };
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps &
  StateProps &
  DispatchProps &
  WithStyles<typeof styles> &
  RouteComponentProps<{ groupId: string | undefined }> & {
    width: Breakpoint;
  };

type State = {
  selectingEmployeeGroup: boolean;
  userWantsEmptyState: boolean;
};

class EmployeeAnnouncementsComponent extends React.PureComponent<Props, State> {
  state = {
    selectingEmployeeGroup: false,
    userWantsEmptyState: false,
  };

  componentDidMount() {
    this.props.actions.loadOrgAnnouncements();
    this.props.actions.loadOrgEmployees();
    this.props.actions.loadOrgEmployeeGroups();
    this.setActiveGroupId();
  }

  componentDidUpdate() {
    this.props.actions.loadOrgAnnouncements();
    this.props.actions.loadOrgEmployees();
    this.props.actions.loadOrgEmployeeGroups();
    this.setActiveGroupId();
  }

  onMobile = () => isWidthDown('sm', this.props.width);

  getGroupsAndAllEmployees = (): EmployeeGroupWithAllOption[] | null => {
    const { orgId, employeeGroups, user } = this.props;
    if (!employeeGroups) {
      return null;
    }

    // IF (01/14/20): Push an artificial 'All Employees' group onto the list, we don't want our
    // users to have to maintain that with an actual group
    return [
      {
        employeeGroupId: allEmployeesKey(orgId),
        name: allEmployeesLabel(user),
        orgId,
      },
      ...employeeGroups,
    ];
  };

  setActiveGroupId = () => {
    const { activeGroupId, announcements, orgId, employeeGroups, history } = this.props;
    if (
      this.onMobile() ||
      !announcements ||
      !employeeGroups ||
      activeGroupId ||
      announcements.length === 0
    ) {
      return;
    }
    history.replace(employeeAnnouncementsRoute(orgId, announcements[0].employeeGroupId));
  };

  handleMessageClick = (employeeGroupId: string) => () => {
    this.showThreadForEmployeeGroupId(employeeGroupId);
    this.setState({ selectingEmployeeGroup: false });
  };

  handleBackClick = () => {
    this.showThreadForEmployeeGroupId(null);
  };

  handleNewAnnouncementToggle = () => {
    this.setState((prev) => ({ selectingEmployeeGroup: !prev.selectingEmployeeGroup }));
  };

  handleNewAnnouncementSelect = (employeeGroup: EmployeeGroupWithAllOption | null) => {
    if (
      employeeGroup &&
      this.props.announcements
        .map((a) => a.employeeGroupId)
        .includes(employeeGroup.employeeGroupId.toString())
    ) {
      this.showThreadForEmployeeGroupId(employeeGroup.employeeGroupId.toString());
    }
  };

  handleBestWayClick = () => {
    this.setState({
      userWantsEmptyState: true,
    });
  };

  turnOffBestWay = () => {
    this.setState({
      userWantsEmptyState: false,
    });
  };

  showThreadForEmployeeGroupId = (employeeGroupId: string | null) => {
    const { history, orgId } = this.props;
    history.replace(employeeAnnouncementsRoute(orgId, employeeGroupId));
  };

  closeGroupAnnouncements = () => {
    this.showThreadForEmployeeGroupId(null);
  };

  renderAnnouncement = (announcement: AnnouncementSummary) => {
    const { classes, width, activeGroupId } = this.props;
    const selected =
      announcement.employeeGroupId === String(activeGroupId) && !isWidthDown('sm', width);
    const contactClass = classNames(classes.contact, { selected });
    const cleanMessageBody = HTMLStripper.strip(announcement.messageBody) || '';

    return (
      <div
        role="presentation"
        key={announcement.employeeGroupId}
        className={`u-padding ${contactClass}`}
        onClick={this.handleMessageClick(announcement.employeeGroupId)}
      >
        <div className="u-display-flex u-justifyContent-spaceBetween u-fontWeight-500">
          <div>{announcement.employeeGroupName}</div>
          <div>{moment(announcement.sentAt).format('M/D/YYYY')}</div>
        </div>
        <div className={classes.messageBody}>
          {cleanMessageBody.length > 100
            ? `${cleanMessageBody.slice(0, 100)}...`
            : cleanMessageBody}
        </div>
      </div>
    );
  };

  renderThreadPane = (activeGroup: EmployeeGroupWithAllOption) => {
    const { classes, orgId } = this.props;
    const onMobile = this.onMobile();

    return (
      <>
        <div className={`u-padding ${classes.threadHeader}`}>
          <div className="u-display-flex u-alignItems-center">
            {onMobile && (
              <MaterialDesignIcon
                iconName={ICON_NAMES.KEYBOARD_ARROW_LEFT}
                className="u-margin-right"
                onClick={this.handleBackClick}
              />
            )}
            <div>
              <div role="presentation" className="typo-h5 typo-link-secondary">
                {activeGroup.name}
              </div>
            </div>
          </div>
        </div>
        <div className={`u-padding-2x ${classes.threadContainer}`}>
          <div className={classes.thread}>
            <LegacyEmployeeGroupAnnouncements
              orgId={orgId}
              employeeGroupId={activeGroup.employeeGroupId}
              onClose={this.closeGroupAnnouncements}
            />
          </div>
        </div>
      </>
    );
  };

  renderEmptyState = () => {
    const { user } = this.props;
    const { userWantsEmptyState } = this.state;

    return (
      <div>
        <UiBreadcrumbHeader
          pageTitle="Announcements"
          primaryActions={[
            userWantsEmptyState
              ? { name: 'Send An Announcement', handler: this.turnOffBestWay }
              : { name: 'Send Your First Announcement', handler: this.handleNewAnnouncementToggle },
          ]}
        />
        <div className="u-maxWidth-750px u-padding u-textAlign-center u-margin-0Auto u-display-flex u-justifyContent-center u-flexDirection-column u-alignItems-center">
          <div className="typo-title u-margin-bottom-2x u-textAlign-left">
            <div className="u-textAlign-center u-margin-y-xl">
              You can use announcements to quickly get in touch with your employees either across
              the whole company or within a given location or role.
            </div>
            <div className="u-maxWidth-550px u-margin-top-lg">
              <div className="typo-bold">Stay connected to deskless employees</div>
              <TextMessage
                isAnnouncement
                senderName={`${user.firstName} ${user.lastName}`}
                sendDateTime={moment().format()}
                messageBody={`Hi ${user.firstName} - This is Mary. This is our new HR phone number. You can text this number to submit any questions regarding payroll, benefits, HR issues, etc.`}
              />
            </div>
            <div className="u-textAlign-right">
              <div className="u-margin-top-lg u-maxWidth-550px u-display-inlineBlock u-textAlign-left">
                <div className="typo-bold">Ask for employee referrals</div>
                <TextMessage
                  isAnnouncement
                  senderName={`${user.firstName} ${user.lastName}`}
                  sendDateTime={moment().format()}
                  messageBody={`Hi ${user.firstName} - As you probably know, we are hiring and would love any referrals that you might have! You can submit them here: https://apply.teamengine.io/refer/efewqgewgrehtqwe3g`}
                />
              </div>
            </div>
            <div className="u-maxWidth-550px u-margin-top-lg">
              <div className="typo-bold">Send reminders for important meetings</div>
              <TextMessage
                isAnnouncement
                senderName={`${user.firstName} ${user.lastName}`}
                sendDateTime={moment().format()}
                messageBody={`Hi ${user.firstName} - Just a reminder that we have a mandatory safety meeting at 2pm on Friday. Please be sure to attend or let us know if you can’t make it for any reason.`}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { orgId, classes, activeGroupId, announcements } = this.props;
    const { selectingEmployeeGroup, userWantsEmptyState } = this.state;
    const groupsAndAllEmployees = this.getGroupsAndAllEmployees();

    const onMobile = this.onMobile();
    const contactClasses = classNames(classes.contacts, {
      [classes.mobile]: onMobile,
      [classes.hidden]: onMobile && (activeGroupId || selectingEmployeeGroup),
    });
    const threadPaneClasses = classNames(classes.threadPane, {
      [classes.mobile]: onMobile,
      [classes.hidden]: onMobile && !activeGroupId && !selectingEmployeeGroup,
    });

    if (!groupsAndAllEmployees || !announcements) {
      return <LoadingMessage />;
    }

    const activeGroup = groupsAndAllEmployees.find((g) => g.employeeGroupId === activeGroupId);
    if ((announcements.length === 0 && !selectingEmployeeGroup) || userWantsEmptyState) {
      return this.renderEmptyState();
    }

    return (
      <div className={classes.container}>
        <UiBreadcrumbHeader
          pageTitle="Announcements"
          customPrimaryAction={
            <UiButton
              size="sm"
              onClick={this.handleNewAnnouncementToggle}
              dataCy={selectingEmployeeGroup ? 'cancel-new-announcement' : 'new-announcement'}
              startIcon={selectingEmployeeGroup ? null : <HeaderButtonAddIcon />}
              variant={selectingEmployeeGroup ? 'secondary' : 'primary'}
            >
              {selectingEmployeeGroup ? 'Cancel' : 'New Announcement'}
            </UiButton>
          }
        />
        <EmployeeNotificationsWarning orgId={orgId} />
        <EmployeeInvalidPhoneNumberWarning orgId={orgId} />
        <div className="u-display-flex u-flexGrow-1 u-overflow-hidden">
          <div className={contactClasses}>
            <div
              role="presentation"
              className="typo-subtitle typo-link u-textAlign-center u-padding-y-sm"
              onClick={this.handleBestWayClick}
            >
              What&apos;s the best way to use announcements?
            </div>
            {announcements ? announcements.map((a) => this.renderAnnouncement(a)) : null}
          </div>
          <div className={threadPaneClasses}>
            {selectingEmployeeGroup ? (
              <div className="u-height-100">
                <LegacyAnnouncementComposer
                  autoFocus
                  orgId={orgId}
                  onChange={this.handleNewAnnouncementSelect}
                  onClose={this.handleNewAnnouncementToggle}
                />
              </div>
            ) : activeGroup ? (
              this.renderThreadPane(activeGroup)
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

export const LegacyEmployeeAnnouncements = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(withWidth()(withRouter(EmployeeAnnouncementsComponent))));
